import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { myApi } from "../api";
import { gaEcommerce } from "@/lib/ga";
import { useMyAuth } from "../contexts/MyAuthContext";

/**
 * /my/cart/action — landing route acionada pelo Assistente de IA quando o
 * cliente confirma uma ação de carrinho (adicionar / remover / alterar
 * quantidade). Reutiliza a lógica nativa de carrinho e o fluxo de login
 * unificado `/login?next=…` para visitantes sem sessão.
 *
 * Parâmetros: ?op=add|remove|set&id=<ID_Produto>&q=<Quantidade>
 */
const VALID_OPS = ["add", "remove", "set"];

export default function MyCartAction() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useMyAuth();
  const [error, setError] = useState("");

  const op = (params.get("op") || "add").toLowerCase();
  const id = Number(params.get("id") || 0);
  const qty = Math.max(1, Number(params.get("q") || 1));

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const next = `/my/cart/action?op=${op}&id=${id}&q=${qty}`;
      window.location.href = `/login?next=${encodeURIComponent(next)}`;
      return;
    }
    if (!id || !VALID_OPS.includes(op)) {
      setError("Pedido inválido.");
      return;
    }
    (async () => {
      try {
        let res;
        if (op === "add") {
          res = await myApi.cartAdd(id, qty, "soma");
          gaEcommerce("add_to_cart", [{ item_id: String(id), item_brand: "IHTP", quantity: qty }], 0);
        } else if (op === "set") {
          res = await myApi.cartUpdateQty(id, qty);
        } else {
          res = await myApi.cartRemove(id);
        }
        if (res && res.success === false) {
          setError(res.message || "Não foi possível concluir a ação no carrinho.");
          return;
        }
        navigate("/my/cart", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.message || e?.message || "Não foi possível concluir a ação no carrinho.");
      }
    })();
  }, [authLoading, user, op, id, qty, navigate]);

  const opLabel = op === "remove" ? "A remover do carrinho…" : op === "set" ? "A atualizar o carrinho…" : "A adicionar ao carrinho…";

  return (
    <div data-testid="my-cart-action-loading" className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <div data-testid="my-cart-action-error" className="text-[#DC2626] text-sm max-w-md">
            <div className="font-semibold mb-1">Erro</div>
            <div>{error}</div>
            <button
              data-testid="my-cart-action-back"
              onClick={() => navigate("/my")}
              className="mt-4 text-[#0F1829] underline text-sm"
            >Voltar à área cliente</button>
          </div>
        ) : (
          <>
            <div className="w-10 h-10 border-2 border-[#0F1829]/20 border-t-[#0F1829] rounded-full animate-spin mx-auto mb-3" />
            <div className="text-sm text-[#6B7280]">{opLabel}</div>
          </>
        )}
      </div>
    </div>
  );
}
